/**
 * EventReportCard Component
 *
 * Card-based display for a single HMC event with modern Tailwind styling.
 * Shows date, time, event details, venue, attendance, and enriched links.
 *
 * Supports:
 * - Double-click (desktop) to open event detail modal
 * - Long-press (mobile) to open event detail modal
 * - Click "View Details" button as fallback
 * - Attendance parsing with total tickets display
 * - Fully booked and new event badges
 */

import React, { useRef, useCallback, useMemo, useState } from 'react';
import type { HMCEvent, MusicGenre } from '../hmcEventsStore';
import { parseAttendance, getAttendanceSummary, formatAttendanceDisplay, getTicketsRemaining, getTicketCapacitySplit, getCleanEventName } from '../utils/attendanceParser';
import { detectEventType, isSportsEvent, type EventTypeResult } from '../utils/eventTypeDetector';
import { parseLocalDate } from '../utils/dateUtils';

// Genre display configuration - white text with darker backgrounds for WCAG contrast
// Note: 'other' is handled specially - we show the actual event category instead
const GENRE_STYLES: Record<Exclude<MusicGenre, 'other'>, { bg: string; text: string; label: string }> = {
  rap: { bg: 'bg-amber-600/80', text: 'text-white', label: 'Hip-Hop' },
  rnb: { bg: 'bg-purple-600/80', text: 'text-white', label: 'R&B' },
  latino: { bg: 'bg-red-600/80', text: 'text-white', label: 'Latino' },
  rock: { bg: 'bg-slate-600/80', text: 'text-white', label: 'Rock' },
  pop: { bg: 'bg-pink-600/80', text: 'text-white', label: 'Pop' },
  country: { bg: 'bg-yellow-600/80', text: 'text-white', label: 'Country' },
  electronic: { bg: 'bg-cyan-600/80', text: 'text-white', label: 'EDM' },
};

/**
 * Get event category styling for card display.
 * Uses centralized eventTypeDetector for detection, applies card-specific styling.
 *
 * @param event - The HMC event to categorize
 * @returns Object with label and style for badge display
 */
const getEventCategory = (event: HMCEvent): { label: string; style: string } => {
  const typeResult = detectEventType(event);

  // Card badge uses consistent dark/light theme styling
  const style = 'bg-dark-bg text-white border border-gray-500';

  // Map detected type to display label
  // Sports types show "Sports" label, others show their specific type
  if (isSportsEvent(event)) {
    return { label: 'Sports', style };
  }

  // Use the label from the detector for other types
  return { label: typeResult.label, style };
};

// Music note icon for genre badge
const MusicNoteIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
  </svg>
);

// Clock icon for event start time
const ClockIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Icons
const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const YoutubeIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const HomeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const AwayIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const TicketIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

// Basketball icon for Rockets games
const BasketballIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.41 0 8 3.59 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
  </svg>
);

// Helper to extract YouTube video ID from URL
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube thumbnail quality fallback chain - not all videos have all qualities
const YOUTUBE_THUMBNAIL_QUALITIES = ['hqdefault.jpg', 'mqdefault.jpg', 'default.jpg'];

// Get YouTube thumbnail URL with proper CDN
const getYouTubeThumbnailUrl = (videoId: string, qualityIndex: number = 0): string => {
  const quality = YOUTUBE_THUMBNAIL_QUALITIES[Math.min(qualityIndex, YOUTUBE_THUMBNAIL_QUALITIES.length - 1)];
  return `https://i.ytimg.com/vi/${videoId}/${quality}`;
};

// YouTube thumbnail component with fallback handling
const YouTubeThumbnailImage: React.FC<{ videoId: string; alt: string }> = ({ videoId, alt }) => {
  const [qualityIndex, setQualityIndex] = useState(0);
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    if (qualityIndex < YOUTUBE_THUMBNAIL_QUALITIES.length - 1) {
      setQualityIndex(prev => prev + 1);
    } else {
      setHasError(true);
    }
  }, [qualityIndex]);

  if (hasError) {
    return (
      <div className="w-full h-32 bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center">
        <YoutubeIcon />
      </div>
    );
  }

  return (
    <img
      src={getYouTubeThumbnailUrl(videoId, qualityIndex)}
      alt={alt}
      className="w-full h-32 object-cover"
      onError={handleError}
    />
  );
};

/**
 * Check if event is a Rockets game.
 * Uses centralized eventTypeDetector for consistent detection.
 */
const isRocketsGame = (event: HMCEvent): boolean => {
  const typeResult = detectEventType(event);
  return typeResult.type === 'rockets';
};

// Extract opponent team name from Rockets game
const getRocketsOpponent = (opponent: string): string => {
  // Pattern: "Rockets v [Team]" or "[Team] v Rockets"
  const vsMatch = opponent.match(/(?:rockets\s+v\s+|vs?\s+)([^/]+)|([^/]+)\s+v\s+rockets/i);
  if (vsMatch) {
    const team = vsMatch[1] || vsMatch[2];
    return team?.trim() || 'Opponent';
  }
  return 'Opponent';
};

interface EventReportCardProps {
  event: HMCEvent;
  isSelected?: boolean;
  onToggleSelection?: (eventId: number) => void;
  onViewDetails?: (event: HMCEvent) => void;
  /** Callback to open assignment modal for PC/EB section editing */
  onEditAssignments?: (event: HMCEvent) => void;
  /** Whether this event is new (not in previous reports) */
  isNewEvent?: boolean;
  /** Total capacity for the event (if known) */
  totalCapacity?: number;
  /** Whether guest view mode is active */
  isGuestView?: boolean;
  /** Maximum tickets to show in guest mode */
  maxGuestTickets?: number;
}

// Long-press threshold in milliseconds
const LONG_PRESS_THRESHOLD = 500;

export const EventReportCard: React.FC<EventReportCardProps> = ({
  event,
  isSelected = false,
  onToggleSelection,
  onViewDetails,
  onEditAssignments,
  isNewEvent = false,
  totalCapacity,
  isGuestView = false,
  maxGuestTickets = 4,
}) => {
  // Long-press state for mobile
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);

  // Parse attendance data
  const attendance = useMemo(() => parseAttendance(event.special_event), [event.special_event]);

  // Calculate tickets remaining using venue-specific capacity
  const ticketInfo = useMemo(() =>
    getTicketsRemaining(event.special_event, event.opponent, event.event_type, event.venue),
    [event.special_event, event.opponent, event.event_type, event.venue]
  );

  // Calculate PC/EB capacity split (50/50)
  const capacitySplit = useMemo(() =>
    getTicketCapacitySplit(event.special_event, event.opponent, event.event_type, event.venue),
    [event.special_event, event.opponent, event.event_type, event.venue]
  );

  // Check if EB is fully booked (availability is based on EB remaining)
  const isFullyBooked = useMemo(() => {
    return capacitySplit.ebRemaining === 0 && capacitySplit.ebUsed > 0;
  }, [capacitySplit.ebRemaining, capacitySplit.ebUsed]);

  // Handle touch start for long-press detection
  const handleTouchStart = useCallback(() => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      if (onViewDetails) {
        onViewDetails(event);
      }
    }, LONG_PRESS_THRESHOLD);
  }, [event, onViewDetails]);

  // Handle touch end - clear timer
  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Handle touch move - cancel long-press if user moves finger
  const handleTouchMove = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Handle double-click for desktop
  const handleDoubleClick = useCallback(() => {
    if (onViewDetails) {
      onViewDetails(event);
    }
  }, [event, onViewDetails]);

  // Format date using centralized date utility to avoid timezone bugs
  const eventDate = parseLocalDate(event.event_date);
  const formattedDate = eventDate?.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) ?? 'Date TBD';
  const dayOfWeek = eventDate?.toLocaleDateString('en-US', { weekday: 'short' }) ?? '';

  // Format time
  const formattedTime = event.kickoff_time || 'TBD';

  // Get event type color
  const getEventTypeStyles = () => {
    switch (event.event_type) {
      case 'dynamo':
        return 'bg-orange-500/10 border-orange-500/30 text-orange-400';
      case 'dash':
        return 'bg-sky-500/10 border-sky-500/30 text-sky-400';
      default:
        return 'bg-gray-500/10 border-gray-500/30 text-dark-muted';
    }
  };

  // Get enrichment status badge - white text, darker backgrounds for WCAG contrast
  const getEnrichmentBadge = () => {
    switch (event.enrichment_status) {
      case 'enriched':
        return 'bg-green-600/80 text-white border-green-500';
      case 'pending':
        return 'bg-yellow-600/80 text-white border-yellow-500';
      case 'failed':
        return 'bg-red-600/80 text-white border-red-500';
      case 'skipped':
        return 'bg-dark-surface/50/80 text-white border-gray-500';
      default:
        return 'bg-dark-surface/50/80 text-white border-gray-500';
    }
  };

  return (
    <div
      className={`
        bg-dark-surface border rounded-xl overflow-hidden cursor-pointer
        transition-all duration-200 hover:shadow-lg hover:border-dark-border/80
        ${getEventTypeStyles()}
        ${isSelected ? 'ring-2 ring-brand-500' : ''}
      `}
      data-testid="event-report-card"
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      title="Double-click or long-press to view details"
    >
      {/* Header - Title + Event Type on left, Badges stacked right */}
      <div className="flex items-start justify-between px-4 py-3 bg-dark-border/30">
        {/* Left side: Checkbox + Title + Event Type */}
        <div className="flex items-start gap-3">
          {/* Selection checkbox */}
          {onToggleSelection && (
            <label className="flex items-center cursor-pointer min-w-[24px] min-h-[44px] pt-0.5">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelection(event.id)}
                className="w-5 h-5 rounded border-dark-border/30 bg-dark-bg text-brand-500 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer"
              />
            </label>
          )}

          <div>
            {/* Event Title on top - cleaned to remove attendance info */}
            {event.opponent && (
              <h3 className="text-lg font-semibold text-white leading-tight">
                {getCleanEventName(event.opponent)}
              </h3>
            )}
            {/* Event Type badge - Shows Sports/Concert/Comedy etc. */}
            {(() => {
              const category = getEventCategory(event);
              return (
                <span
                  className={`event-type-badge inline-block mt-1.5 px-2 py-0.5 rounded text-xs font-medium uppercase ${category.style}`}
                >
                  {category.label}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Badges - Stacked vertically, aligned right */}
        <div className="flex flex-col items-end gap-1.5">
          {/* New Event Badge - white text, darker background */}
          {isNewEvent && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-brand-600/80 text-white border border-brand-500">
              NEW
            </div>
          )}

          {/* Music Genre Badge - only show for known genres (not 'other') */}
          {event.music_genre && event.music_genre !== 'other' && (
            <div
              className={`
                flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                ${GENRE_STYLES[event.music_genre].bg} ${GENRE_STYLES[event.music_genre].text}
                border border-white/30
              `}
            >
              <MusicNoteIcon />
              <span>{GENRE_STYLES[event.music_genre].label}</span>
            </div>
          )}

          {/* Event Category Badge - removed as primary badge now shows category */}

          {/* Fully Booked Badge - white text, darker background */}
          {isFullyBooked && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-600/80 text-white border border-red-500">
              SOLD OUT
            </div>
          )}

          {/* USI Suite Badge - Shows on all HMC events (they are all suite events) */}
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-brand-500 to-brand-600 text-white border border-brand-400 shadow-sm"
            title="USI Toyota Center Suite"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14h-2v-4H8v-2h2V9h2v2h2v2h-2v4zm4-8h-2V7h2v2z"/>
            </svg>
            <span>USI Suite</span>
          </div>
        </div>
      </div>

      {/* Date & Time Section */}
      <div className="px-4 py-2 bg-dark-border/20 border-t border-dark-border/30">
        <div className="flex items-center gap-3">
          <div className="text-sm text-dark-muted">
            <span className="font-medium">{dayOfWeek}</span> {formattedDate}
          </div>
          {/* Time badge */}
          <div className="px-2 py-0.5 bg-dark-bg rounded text-xs font-medium text-white">
            {formattedTime}
          </div>
          {/* Scraped event start time (more accurate than kickoff_time) */}
          {event.event_start_time && (
            <div className="flex items-center gap-1 text-sm text-emerald-400">
              <ClockIcon />
              <span>{event.event_start_time}</span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">

        {/* Score (if available) */}
        {event.score && (
          <div className="text-sm">
            <span className="text-dark-muted">Score:</span>{' '}
            <span className="text-white font-medium">{event.score}</span>
          </div>
        )}

        {/* Guest View: YouTube Thumbnail or Sports Info */}
        {isGuestView && (
          <div className="space-y-3">
            {/* For Rockets games: Show sports info card */}
            {isRocketsGame(event) ? (
              <div className="p-4 bg-gradient-to-r from-red-900/40 to-gray-900/40 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                    <BasketballIcon />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-red-300 uppercase tracking-wide mb-1">NBA Basketball</div>
                    <div className="text-lg font-bold text-white">Houston Rockets</div>
                    <div className="text-sm text-dark-muted">vs {getRocketsOpponent(event.opponent || '')}</div>
                  </div>
                </div>
                {event.venue && (
                  <div className="mt-3 pt-3 border-t border-red-500/20 text-sm text-dark-muted">
                    <span className="text-red-300">Venue:</span> {event.venue}
                  </div>
                )}
              </div>
            ) : event.youtube_url ? (
              /* For concerts: Show YouTube thumbnail */
              <div className="rounded-lg overflow-hidden border border-dark-border/30">
                <a
                  href={event.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative group"
                >
                  {extractYouTubeId(event.youtube_url) ? (
                    <YouTubeThumbnailImage
                      videoId={extractYouTubeId(event.youtube_url)!}
                      alt={`${event.opponent || 'Event'} video thumbnail`}
                    />
                  ) : (
                    <div className="w-full h-32 bg-red-900/30 flex items-center justify-center">
                      <YoutubeIcon />
                    </div>
                  )}
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-all duration-150">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </a>
                <div className="px-3 py-2 bg-dark-border/30">
                  <span className="text-xs text-dark-muted">Watch on YouTube</span>
                </div>
              </div>
            ) : null}

            {/* Tickets Available - Guest view simplified (EB-based) */}
            <div className="flex items-center justify-center gap-2 p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg">
              <TicketIcon />
              <span className="text-sm font-semibold text-brand-300">
                {capacitySplit.ebRemaining === 0 ? 'Sold Out' : `${Math.min(capacitySplit.ebRemaining, maxGuestTickets)} Tickets Available`}
              </span>
            </div>

            {/* Request Tickets / Standby Button */}
            <button
              onClick={() => onViewDetails?.(event)}
              className={`w-full py-3 rounded-lg font-semibold text-sm transition-all duration-150 min-h-[44px] ${
                capacitySplit.ebRemaining === 0
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                  : 'bg-brand-500 text-white hover:bg-brand-600'
              }`}
            >
              {capacitySplit.ebRemaining === 0 ? 'Request Standby' : 'Request Tickets'}
            </button>
          </div>
        )}

        {/* Admin View: Attendance section with PC/EB split */}
        {!isGuestView && attendance.entries.length > 0 && (
          <div className="p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <UsersIcon />
                <span className="text-xs font-semibold text-brand-300 uppercase tracking-wide">Attendance</span>
              </div>
              <div className="flex items-center gap-3">
                {/* PC tickets */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full">
                  <span className="text-xs font-bold text-purple-300">PC</span>
                  <span className="text-sm font-bold text-purple-200">{capacitySplit.pcUsed}/{capacitySplit.pcCapacity}</span>
                </div>
                {/* EB tickets */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 border border-brand-500/30 rounded-full">
                  <span className="text-xs font-bold text-blue-300">EB</span>
                  <span className="text-sm font-bold text-blue-200">{capacitySplit.ebUsed}/{capacitySplit.ebCapacity}</span>
                </div>
              </div>
            </div>
            {/* Attendee list grouped by section */}
            <div className="space-y-1 px-3">
              {attendance.entries.slice(0, 4).map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-brand-300">
                    {entry.name}
                    {entry.company && <span className="text-brand-400 text-xs ml-1">({entry.company})</span>}
                    {entry.section && (
                      <span className={`text-[10px] ml-1 px-1 py-0.5 rounded ${
                        entry.section === 'PC' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                      }`}>{entry.section}</span>
                    )}
                  </span>
                  <span className="text-brand-200 font-medium">{entry.tickets}</span>
                </div>
              ))}
              {attendance.entries.length > 4 && (
                <div className="text-xs text-brand-400 italic">
                  +{attendance.entries.length - 4} more attendees
                </div>
              )}
            </div>
          </div>
        )}
        {/* Show PC/EB capacity even if no attendance entries - Admin view only */}
        {!isGuestView && attendance.entries.length === 0 && !attendance.cleanText && (
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 rounded">
              <span className="text-xs text-purple-400">PC</span>
              <span className="text-purple-300">0/{capacitySplit.pcCapacity}</span>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-brand-500/20 rounded">
              <span className="text-xs text-blue-400">EB</span>
              <span className="text-blue-300">0/{capacitySplit.ebCapacity}</span>
            </div>
          </div>
        )}
        {/* Show raw notes if no structured attendance but has special_event - Admin view only */}
        {!isGuestView && !attendance.entries.length && attendance.cleanText && (
          <div className="p-3 bg-dark-border/30 border border-dark-border/30 rounded-xl">
            <p className="text-sm text-dark-muted">{attendance.cleanText}</p>
          </div>
        )}

        {/* Links */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {/* Event URL */}
          {event.event_url && (
            <a
              href={event.event_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-border text-dark-text hover:text-white rounded-lg text-sm transition-all duration-150 min-h-[44px]"
            >
              <ExternalLinkIcon />
              <span>Event Link</span>
            </a>
          )}

          {/* YouTube link */}
          {event.youtube_url && (
            <a
              href={event.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 hover:text-red-300 rounded-lg text-sm transition-all duration-150 min-h-[44px]"
            >
              <YoutubeIcon />
              <span>Watch</span>
            </a>
          )}

{/* Enrichment badge removed per user request */}
        </div>
      </div>

      {/* Footer - View details with Last Updated date (hidden in guest view) */}
      {onViewDetails && (
        <div className="px-4 py-3 bg-dark-border/20 border-t border-dark-border/30 flex items-center justify-between">
          {/* Last Updated - italicized, hidden in guest view */}
          {!isGuestView && event.updated_at && (
            <span className="text-xs text-dark-muted italic hidden sm:block">
              Last Updated {new Date(event.updated_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
            </span>
          )}
          {(isGuestView || !event.updated_at) && <span className="hidden sm:block" />}

          {/* Action buttons container - flex on mobile to fit both */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {/* Edit Assignments button - only in admin view */}
            {!isGuestView && onEditAssignments && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditAssignments(event);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500 hover:text-white border border-purple-500/30 rounded-lg text-sm font-medium transition-all duration-150 min-h-[44px]"
                title="Edit PC/EB section assignments"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="hidden xs:inline">Assign</span>
              </button>
            )}

            {/* View Details button */}
            <button
              onClick={() => onViewDetails(event)}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand-500/20 text-brand-400 hover:bg-brand-500 hover:text-white border border-brand-500/30 rounded-lg text-sm font-medium transition-all duration-150 min-h-[44px]"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="hidden xs:inline">View</span> Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventReportCard;
